import React, { useState, useEffect } from "react";
import { productAPI } from "../lib/api";
import { formatCurrency, debounce } from "../lib/utils";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Loader2,
  Package,
  Filter,
  X,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  "Printing",
  "Fabrication",
  "Event Services",
  "Logistics",
  "Equipment",
  "Gifts",
  "Merchandise",
  "Government",
  "Event Package",
];

// Category to is_service mapping
const CATEGORY_IS_SERVICE = {
  "Printing": false,
  "Fabrication": true,
  "Event Services": true,
  "Logistics": true,
  "Equipment": false,
  "Gifts": false,
  "Merchandise": false,
  "Government": true,
  "Event Package": true,
};

const initialFormData = {
  name: "",
  category: "",
  hsn_sac_code: "",
  description: "",
  unit: "NOS",
  default_price: 0,
  tax_rate: 18,
  is_service: false,
};

export default function ProductsPage() {
  const { canEdit, isAdmin } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, [categoryFilter]);

  const fetchProducts = async (searchTerm = "") => {
    try {
      const params = { search: searchTerm };
      if (categoryFilter) {
        params.category = categoryFilter;
      }
      const response = await productAPI.list(params);
      setProducts(response.data);
    } catch (error) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = debounce((term) => {
    fetchProducts(term);
  }, 300);

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearch(term);
    debouncedSearch(term);
  };

  const handleCategoryFilterChange = (value) => {
    setCategoryFilter(value === "all" ? "" : value);
  };

  const clearFilters = () => {
    setSearch("");
    setCategoryFilter("");
    fetchProducts("");
  };

  const openNewDialog = () => {
    setEditingProduct(null);
    setFormData(initialFormData);
    setDialogOpen(true);
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || "",
      category: product.category || "",
      hsn_sac_code: product.hsn_sac_code || "",
      description: product.description || "",
      unit: product.unit || "NOS",
      default_price: product.default_price || 0,
      tax_rate: product.tax_rate || 18,
      is_service: product.is_service || false,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("Product name is required");
      return;
    }
    
    if (!formData.hsn_sac_code.trim()) {
      toast.error("HSN/SAC code is required");
      return;
    }

    setSaving(true);
    try {
      if (editingProduct) {
        await productAPI.update(editingProduct.id, formData);
        toast.success("Product updated successfully");
      } else {
        await productAPI.create(formData);
        toast.success("Product created successfully");
      }
      setDialogOpen(false);
      fetchProducts(search);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`)) {
      return;
    }

    try {
      await productAPI.delete(product.id);
      toast.success("Product deleted");
      fetchProducts(search);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete product");
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? parseFloat(value) || 0 : value,
    }));
  };

  const handleCategoryChange = (value) => {
    const isService = CATEGORY_IS_SERVICE[value] ?? false;
    setFormData((prev) => ({
      ...prev,
      category: value,
      is_service: isService,
    }));
  };

  // Get category badge color
  const getCategoryColor = (category) => {
    const colors = {
      "Printing": "bg-blue-100 text-blue-800",
      "Fabrication": "bg-purple-100 text-purple-800",
      "Event Services": "bg-green-100 text-green-800",
      "Logistics": "bg-orange-100 text-orange-800",
      "Equipment": "bg-slate-100 text-slate-800",
      "Gifts": "bg-pink-100 text-pink-800",
      "Merchandise": "bg-cyan-100 text-cyan-800",
      "Government": "bg-red-100 text-red-800",
      "Event Package": "bg-indigo-100 text-indigo-800",
    };
    return colors[category] || "bg-gray-100 text-gray-800";
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="products-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Products & Services</h1>
          <p className="text-slate-500">Manage your product and service catalog</p>
        </div>
        {canEdit() && (
          <Button onClick={openNewDialog} data-testid="add-product-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Product
          </Button>
        )}
      </div>

      {/* Search & Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name or HSN/SAC code..."
                value={search}
                onChange={handleSearchChange}
                className="pl-10"
                data-testid="search-products"
              />
            </div>
            <div className="flex gap-2">
              <Select value={categoryFilter || "all"} onValueChange={handleCategoryFilterChange}>
                <SelectTrigger className="w-[180px]" data-testid="category-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(search || categoryFilter) && (
                <Button variant="ghost" size="icon" onClick={clearFilters}>
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-semibold text-slate-900 mb-1">No products found</h3>
              <p className="text-slate-500 mb-4">
                {search || categoryFilter ? "Try different filters" : "Get started by adding your first product"}
              </p>
              {canEdit() && !search && !categoryFilter && (
                <Button onClick={openNewDialog}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Product
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>HSN/SAC</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                  <TableHead className="text-right">GST %</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{product.name}</div>
                        {product.description && product.description !== product.name && (
                          <div className="text-sm text-slate-500 truncate max-w-[200px]">
                            {product.description}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {product.category && (
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(product.category)}`}>
                          {product.category}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{product.hsn_sac_code}</TableCell>
                    <TableCell>
                      <Badge variant={product.is_service ? "secondary" : "outline"}>
                        {product.is_service ? "Service" : "Product"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(product.default_price)}
                    </TableCell>
                    <TableCell className="text-right">{product.tax_rate}%</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canEdit() && (
                            <DropdownMenuItem onClick={() => openEditDialog(product)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                          )}
                          {isAdmin() && (
                            <DropdownMenuItem
                              onClick={() => handleDelete(product)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Product Count */}
      {!loading && products.length > 0 && (
        <div className="text-sm text-slate-500 text-center">
          Showing {products.length} product{products.length !== 1 ? 's' : ''}
          {categoryFilter && ` in ${categoryFilter}`}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingProduct ? "Edit Product" : "Add New Product"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Product or service name"
                data-testid="product-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select value={formData.category || ""} onValueChange={handleCategoryChange}>
                <SelectTrigger data-testid="category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="hsn_sac_code">HSN/SAC Code *</Label>
                <Input
                  id="hsn_sac_code"
                  name="hsn_sac_code"
                  value={formData.hsn_sac_code}
                  onChange={handleInputChange}
                  placeholder="9989"
                  className="font-mono"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="unit">Unit</Label>
                <Input
                  id="unit"
                  name="unit"
                  value={formData.unit}
                  onChange={handleInputChange}
                  placeholder="NOS"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Brief description (optional)"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="default_price">Default Price (₹)</Label>
                <Input
                  id="default_price"
                  name="default_price"
                  type="number"
                  step="0.01"
                  value={formData.default_price}
                  onChange={handleInputChange}
                />
                <p className="text-xs text-slate-500">Can be overridden during invoice</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="tax_rate">GST Rate (%)</Label>
                <Input
                  id="tax_rate"
                  name="tax_rate"
                  type="number"
                  step="0.01"
                  value={formData.tax_rate}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label htmlFor="is_service">Product Type</Label>
                <p className="text-sm text-slate-500">
                  {formData.is_service ? "This is a Service" : "This is a Product"}
                </p>
              </div>
              <Switch
                id="is_service"
                checked={formData.is_service}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, is_service: checked }))
                }
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-product-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : editingProduct ? (
                  "Update Product"
                ) : (
                  "Add Product"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
